// @flow

import {
  type IncomingPayment,
  type OpenedIncomingPayment,
  getAddress,
  getCustomerEmail,
  getCustomerName,
  getDocument,
  getPhoneNumber,
  getReceipts,
  getPaymentType,
  getPaymentTypeCode,
  getCountry,
  getMerchant,
  getPersonType,
} from '@tectonics/prs/incoming-payment';

import { getLookAndFeel} from '@tectonics/prs/merchant';

import {
  getZipcode,
  getState as getAddressState,
  getCity,
  getStreetName,
  getStreetNumber,
  getStreetComplement,
} from '@tectonics/prs/address';

import { getDocumentTypeInferredFromValue as getBrazilianDocumentTypeInferredFromValue } from '@tectonics/prs/customer/brazil';
import { getLowerInstalmentKey } from '@tectonics/prs/receipts';
import type { UserInputValues } from '@tectonics/prs/user-input-values/types';
import { decideArgentineDocument, filterArgentineCashBankCode } from '@tectonics/prs/argentina';

export function getOpenedPaymentBasedInitialUserInputValues(incomingPayment: OpenedIncomingPayment): UserInputValues {
  return {
    ...getOpenedCommonsInitialValues(incomingPayment),
    ...getCountryInitialValues(incomingPayment),
  };
}

function filterCustomerName(incomingPayment: OpenedIncomingPayment): string {
  const merchant = getMerchant(incomingPayment);
  const customerName = getCustomerName(incomingPayment);

  if (customerName === 'PAYMENT REQUEST CUSTOMER' && getLookAndFeel(merchant).toLowerCase() === 'uber') {
    return '';
  }

  return customerName || '';
}

function getOpenedCommonsInitialValues(incomingPayment: OpenedIncomingPayment): UserInputValues {
  return {
    selectedPaymentMethod: '',
    captchaToken: '',
    customerName: filterCustomerName(incomingPayment),
    customerEmail: getCustomerEmail(incomingPayment) || '',
    cardHolderName: '',
    cardNumber: '',
    cardExpiryDate: '',
    cardSecurityCode: '',
    selectedInstalment: getLowerInstalmentKey(getReceipts(incomingPayment)),
    acceptVisaTerms: true,
    acceptTerms: false,
    selectedBankCode: '',
    selectedTefBank: '',
    selectedSafetypayOption: getInitialSafetypayOption(incomingPayment),
  };
}

function getInitialSafetypayOption(incomingPayment: IncomingPayment): string {
  const paymentTypeCode = getPaymentTypeCode(incomingPayment);

  return (paymentTypeCode === 'safetypay-cash' || paymentTypeCode === 'safetypay-online')
    ? paymentTypeCode
    : '';
}

function getCountryInitialValues(incomingPayment: OpenedIncomingPayment): UserInputValues {
  switch (getCountry(incomingPayment)) {
    case 'br':
      return {
        brazilianDocument: getDocumentOrEmpty(incomingPayment),
        brazilianDocumentType: getBrazilianDocumentTypeInferredFromValue(getDocument(incomingPayment)),
        brazilianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        brazilianZipcode: getZipcodeOrEmpty(incomingPayment),
        brazilianState: getAddressState(getAddress(incomingPayment)),
        brazilianCity: getCity(getAddress(incomingPayment)),
        brazilianAddress: getStreetName(getAddress(incomingPayment)),
        brazilianStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        brazilianStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        selectedEWallet: '',
      };

    case 'mx':
      return {
        mexicanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        mexicanZipcode: getZipcodeOrEmpty(incomingPayment),
        mexicanState: getAddressState(getAddress(incomingPayment)),
        mexicanCity: getCity(getAddress(incomingPayment)),
        mexicanAddress: getStreetName(getAddress(incomingPayment)),
        mexicanStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        mexicanStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        selectedEWallet: 'mercadopago',
      };

    case 'ar': {
      const argentineDocument = getDocumentOrEmpty(incomingPayment);

      return {
        argentineDocument: argentineDocument,
        argentineDocumentType: decideArgentineDocument(argentineDocument),
        argentinePhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        argentineZipcode: getZipcodeOrEmpty(incomingPayment),
        argentineState: getAddressState(getAddress(incomingPayment)),
        argentineCity: getCity(getAddress(incomingPayment)),
        argentineAddress: getStreetName(getAddress(incomingPayment)),
        argentineStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        argentineStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        selectedBankCode: filterArgentineCashBankCode(getPaymentTypeCode(incomingPayment)),
        selectedEWallet: 'mercadopago',
      };
    }

    case 'co': {
      const personType = getPersonType(incomingPayment);

      return {
        colombianDocument: getDocumentOrEmpty(incomingPayment),
        colombianDocumentType: personType === 'business' ? 'NIT' : 'CC',
        colombianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        colombianZipcode: getZipcodeOrEmpty(incomingPayment),
        colombianState: getAddressState(getAddress(incomingPayment)),
        colombianCity: getCity(getAddress(incomingPayment)),
        colombianAddress: getStreetName(getAddress(incomingPayment)),
        colombianStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        colombianStreetComplement: getStreetComplement(getAddress(incomingPayment)),
        selectedEftBank: '',
        selectedEWallet: 'nequi',
      };
    }

    case 'cl':
      return {
        chileanDocumentType: 'RUT',
        chileanDocument: getDocumentOrEmpty(incomingPayment),
        chileanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        chileanZipcode: getZipcodeOrEmpty(incomingPayment),
        chileanState: getAddressState(getAddress(incomingPayment)),
        chileanCity: getCity(getAddress(incomingPayment)),
        chileanAddress: getStreetName(getAddress(incomingPayment)),
        chileanStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        chileanStreetComplement: getStreetComplement(getAddress(incomingPayment)),
      };

    case 'pe':
      return {
        peruvianDocument: getDocumentOrEmpty(incomingPayment),
        peruvianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        peruvianZipcode: getZipcodeOrEmpty(incomingPayment),
        peruvianState: getAddressState(getAddress(incomingPayment)),
        peruvianCity: getCity(getAddress(incomingPayment)),
        peruvianAddress: getStreetName(getAddress(incomingPayment)),
        peruvianStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        peruvianStreetComplement: getStreetComplement(getAddress(incomingPayment)),
      };

    case 'ec':
      return {
        ecuadorianDocument: getDocumentOrEmpty(incomingPayment),
        ecuadorianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        ecuadorianZipcode: getZipcodeOrEmpty(incomingPayment),
        ecuadorianState: getAddressState(getAddress(incomingPayment)),
        ecuadorianCity: getCity(getAddress(incomingPayment)),
        ecuadorianAddress: getStreetName(getAddress(incomingPayment)),
        ecuadorianStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        ecuadorianStreetComplement: getStreetComplement(getAddress(incomingPayment)),
      };

    case 'bo':
      return {
        bolivianDocument: getDocumentOrEmpty(incomingPayment),
        bolivianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        bolivianZipcode: getZipcodeOrEmpty(incomingPayment),
        bolivianState: getAddressState(getAddress(incomingPayment)),
        bolivianCity: getCity(getAddress(incomingPayment)),
        bolivianAddress: getStreetName(getAddress(incomingPayment)),
        bolivianStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        bolivianStreetComplement: getStreetComplement(getAddress(incomingPayment)),
      };

    case 'uy': {
      return {
        uruguayanDocumentType: 'CI',
        uruguayanDocument: getDocumentOrEmpty(incomingPayment),
        uruguayanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        uruguayanZipcode: getZipcodeOrEmpty(incomingPayment),
        uruguayanState: getAddressState(getAddress(incomingPayment)),
        uruguayanCity: getCity(getAddress(incomingPayment)),
        uruguayanAddress: getStreetName(getAddress(incomingPayment)),
        uruguayanStreetNumber: getStreetNumber(getAddress(incomingPayment)),
        uruguayanStreetComplement: getStreetComplement(getAddress(incomingPayment)),
      };
    }

    default:
      throw new Error(`Country ${getCountry(incomingPayment)} not supported`);
  }
}

function getDocumentOrEmpty(incomingPayment: IncomingPayment): string {
  return getDocument(incomingPayment) || '';
}

function getPhoneNumberOrEmpty(incomingPayment: IncomingPayment): string {
  return getPhoneNumber(incomingPayment) || '';
}

function getZipcodeOrEmpty(incomingPayment: IncomingPayment): string {
  return getZipcode(getAddress(incomingPayment)) || '';
}

export function getPendingPaymentBasedInitialUserInputValues(incomingPayment: IncomingPayment): UserInputValues {
  return {
    selectedPaymentMethod: getPaymentType(incomingPayment),
  };
}

export function getConfirmedPaymentBasedInitialUserInputValues(incomingPayment: IncomingPayment): UserInputValues {
  return {
    selectedPaymentMethod: getPaymentType(incomingPayment),
  };
}

export function getCanceledPaymentBasedInitialUserInputValues(incomingPayment: IncomingPayment): UserInputValues {
  return {
    selectedPaymentMethod: getPaymentType(incomingPayment),
    selectedInstalment: getLowerInstalmentKey(getReceipts(incomingPayment)),
  };
}

export function getPaymentBasedInitialUserInputValues(incomingPayment: IncomingPayment): UserInputValues {
  switch (incomingPayment.status) {
    case 'OP':
      return getOpenedPaymentBasedInitialUserInputValues(incomingPayment);

    case 'PE':
      return getPendingPaymentBasedInitialUserInputValues(incomingPayment);

    case 'CO':
      return getConfirmedPaymentBasedInitialUserInputValues(incomingPayment);

    case 'CA':
      return getCanceledPaymentBasedInitialUserInputValues(incomingPayment);

    default:
      return {};
  }
}
